import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, forkJoin } from 'rxjs';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { messageSeverityEnum } from '../../Misc/enums/message-severity.enum';
import { TagGroupModel } from '../models/tag-group.model';
import { TagGroupsApi } from '../../../api/tags/tag-groups.api';
import { CommonApiErrorEnum } from '../../../api/genericApi/enums/common-api-error.enum';
import { MessageService } from 'primeng/api';
import { map } from 'rxjs/operators';

@Injectable()
export class TagGroupsService {
    private tagGroupListItems: TagGroupModel[];
    private tagGroupListItemsProvider: BehaviorSubject<TagGroupModel[]>;
    private tagGroupIdMap: Map<number, TagGroupModel> = new Map<number, TagGroupModel>();
    // Next time the cache needs updated
    private nextUpdateTime: number = 0;
    private refreshPromise: Promise<void> = null;

    constructor(
        private tagGroupsApi: TagGroupsApi,
        private messageService: MessageService,
        private localizationService: LocalizationService
    ) {
        this.tagGroupListItemsProvider = new BehaviorSubject<TagGroupModel[]>(null);
        this.updateTagGroupListItems();
    }

    /**
     * Returns an observable with the available tag group models
     */
    getTagGroupsList(): Observable<TagGroupModel[]> {
        return this.tagGroupListItemsProvider;
    }

    /**
     * Fetches the list of available tag groups and pushes it to provider
     */
    updateTagGroupListItems(force: boolean = false): Promise<void> {
        if (!this.refreshPromise) {
            this.refreshPromise = new Promise((resolve) => {
                const newTime = +new Date();
                if (!force && newTime < this.nextUpdateTime) {
                    resolve();

                    // Have to mark it null asyncronously, otherwise it marks it null syncronously and everything gets weird
                    setTimeout(() => {
                        this.refreshPromise = null;
                    }, 0);

                    return;
                }
                this.tagGroupsApi.getList().subscribe(
                    listItems => {
                        this.tagGroupIdMap = new Map<number, TagGroupModel>();
                        this.tagGroupListItems = listItems.map<TagGroupModel>((listItem) => {
                            const tagModel = new TagGroupModel(listItem);
                            this.tagGroupIdMap.set(tagModel.id, tagModel);

                            return tagModel;
                        });

                        this.tagGroupListItemsProvider.next(this.tagGroupListItems);
                        resolve();
                        this.refreshPromise = null;
                    }
                );

                // 60,000 ms is one minute
                this.nextUpdateTime = newTime + 60000;
            });
        }
        return this.refreshPromise;
    }

    /**
     * Returns a tagGroupModel with the requested id
     */
    async getTagGroupModelById(id: number): Promise<TagGroupModel> {
        let tag = this.tagGroupIdMap.get(id);
        if (!tag) {
            await this.updateTagGroupListItems();
            tag = this.tagGroupIdMap.get(id);
        }

        return tag;
    }

    /**
     * Creates a new tag group
     */
    createTagGroup(tagGroup: TagGroupModel): Observable<number> {
        const apiModel = tagGroup.toApiModel();

        const request = this.tagGroupsApi.create(apiModel);
        request.subscribe({
            next: () => {
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: this.localizationService.transform('Tags.tagGroup.createSucessHeading'),
                    detail: this.localizationService.transform('Tags.tagGroup.createSucessMessage', {
                        name: tagGroup.name
                    })
                });
                this.updateTagGroupListItems(true);
            },
            error: () => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: this.localizationService.transform('Tags.tagGroup.createErrorHeading'),
                    detail: this.localizationService.transform('Tags.tagGroup.createErrorMessage', {
                        name: tagGroup.name
                    })
                });
                this.updateTagGroupListItems(true);
            }
        });

        return request;
    }

    /**
     * Saves an existing tag group
     */
    saveTagGroup(tagGroup: TagGroupModel): Observable<null> {
        const apiModel = tagGroup.toApiModel();

        const request = this.tagGroupsApi.update(apiModel);
        request.subscribe({
            next: () => {
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: this.localizationService.transform('Tags.tagGroup.updateSucessHeading'),
                    detail: this.localizationService.transform('Tags.tagGroup.updateSucessMessage', {
                        name: tagGroup.name
                    })
                });
                this.updateTagGroupListItems(true);
            },
            error: (error) => {
                let handleError = false;
                if (error && error.error && error.error.details) {
                    for (const errorDetail of error.error.details) {
                        switch (errorDetail.code) {
                            case CommonApiErrorEnum.duplicateName:
                                this.messageService.add({
                                    severity: messageSeverityEnum.error,
                                    summary: this.localizationService.transform('Tags.tagGroup.updateErrorHeading'),
                                    detail: this.localizationService.transform('Tags.tagGroup.updateErrorMessage.duplicate')
                                });
                                handleError = true;
                                break;
                        }
                    }
                }

                if (!handleError) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: this.localizationService.transform('Tags.tagGroup.updateErrorHeading'),
                        detail: this.localizationService.transform('Tags.tagGroup.updateErrorMessage', {
                            name: tagGroup.name
                        })
                    });
                }
                this.updateTagGroupListItems(true);
            }
        });

        return request;
    }

    /**
     * Permanently deletes a tag group
     */
    deleteTagGroups(tagGroups: TagGroupModel[]): Observable<null> {
        const requests = tagGroups.map(tagGroup => this.tagGroupsApi.delete(tagGroup.toApiModel()));
        const request = forkJoin(requests).pipe(
            map(results => null)
        );

        request.subscribe({
            next: () => {
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: this.localizationService.transform('Tags.tagGroup.deleteSucessHeading'),
                    detail: this.localizationService.transform('Tags.tagGroup.deleteSucessMessage', { count: tagGroups.length })
                });
                this.updateTagGroupListItems(true);
            },
            error: () => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: this.localizationService.transform('Tags.tagGroup.deleteErrorHeading'),
                    detail: this.localizationService.transform('Tags.tagGroup.deleteErrorMessage', { count: tagGroups.length })
                });
                this.updateTagGroupListItems(true);
            }
        });

        return request;
    }
}
